import type { Redirect } from '@tanstack/react-router';
import type { FeatureFlagKey } from '@vizit/constants';
import type { LDClient } from 'launchdarkly-react-client-sdk';
import type { FeatureFlagContextValue } from '../provider/provider';

type FeatureFlagClientSetup = {
  client: LDClient;
  context: FeatureFlagContextValue | null;
};

let resolveClientReady: (setup: FeatureFlagClientSetup) => void;
let clientReadyPromise = new Promise<FeatureFlagClientSetup>((resolve) => {
  resolveClientReady = resolve;
});

/**
 * Called by the FeatureFlagProvider when the LaunchDarkly client is ready.
 * This resolves the internal promise that `requireFeatureFlags` waits on.
 */
export function setFeatureFlagClient(client: LDClient, context: FeatureFlagContextValue | null = null): void {
  resolveClientReady({ client, context });
}

/**
 * Resets the client ready promise. Used for testing.
 * @internal
 */
export function resetFeatureFlagClient(): void {
  clientReadyPromise = new Promise<FeatureFlagClientSetup>((resolve) => {
    resolveClientReady = resolve;
  });
}

/**
 * Waits for the LaunchDarkly client to be ready and have loaded feature flags.
 * Use this outside of React components when you need to ensure flags are available.
 * @param flags - The feature flags to check. If no flags are provided, the function will just wait for the client to be ready.
 * @param timeoutMs - The timeout in milliseconds. Defaults to 5000.
 * @param error - The error to reject with if the flags are not truthy after loading. Defaults to a new Error('Not allowed').
 * @returns A promise that resolves if the flags are truthy after loading.
 */
export async function requireFeatureFlags(
  flags: FeatureFlagKey[] = [],
  { error = new Error('Not allowed'), timeoutMs = 5000 }: { error?: Error | Redirect; timeoutMs?: number } = {},
): Promise<void> {
  let timer: NodeJS.Timeout = 0 as unknown as NodeJS.Timeout;
  const context = await Promise.race([
    // wait for the client to be ready and initialized
    clientReadyPromise
      .then(async ({ client, context }) => {
        await client.waitForInitialization(timeoutMs / 1000);
        return context;
      })
      .catch((error) => {
        console.error('LaunchDarkly initialization failed:', error);
        return null;
      }),
    new Promise<null>((resolve) => {
      timer = setTimeout(() => {
        console.error(`LaunchDarkly initialization timed out after ${timeoutMs}ms`);
        resolve(null);
      }, timeoutMs);
    }),
  ]);
  clearTimeout(timer);

  if (!flags.every((flag) => context?.getFlag(flag, false))) {
    throw error;
  }
}

/**
 * Reads a single feature flag value asynchronously, outside of React.
 * Safe to use in TanStack Router `beforeLoad`. Returns `defaultValue` if
 * LaunchDarkly is unavailable or times out.
 * @param flag - The feature flag key to check
 * @param defaultValue - The default value if the flag is not available
 * @param timeoutMs - The timeout in milliseconds. Defaults to 5000.
 * @returns The flag value
 */
export async function getFeatureFlag<T>(
  flag: FeatureFlagKey,
  defaultValue: T,
  { timeoutMs = 5000 }: { timeoutMs?: number } = {},
): Promise<T> {
  let timer: NodeJS.Timeout = 0 as unknown as NodeJS.Timeout;
  const context = await Promise.race([
    clientReadyPromise
      .then(async ({ client, context }) => {
        await client.waitForInitialization(timeoutMs / 1000);
        return context;
      })
      .catch(() => null),
    new Promise<null>((resolve) => {
      timer = setTimeout(() => resolve(null), timeoutMs);
    }),
  ]);
  clearTimeout(timer);

  return context?.getFlag(flag, defaultValue) ?? defaultValue;
}
